/**
 * GlobalSearchDialog — Command-palette-style global search
 *
 * Searches across documents, issues, sprints, and users via the search-service.
 * Results grouped by project, sorted by last-edited timestamp.
 * Supports keyboard navigation (arrow keys, Enter, Escape).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  BookOpen,
  FlaskConical,
  Bug,
  Sparkles,
  CheckCircle2,
  Circle,
  User,
  Loader2,
  X,
  Zap,
  FolderOpen,
  Mail,
} from 'lucide-react';
import { searchApi, type GlobalSearchResponse, type RecentItemsResponse, type SearchHit, type DocumentHit, type IssueHit, type UserHit, type SprintHit, type EmailThreadHit, type TopicHit } from '../../api/search';
import { useProject, getProjectShortId } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useTopicBar } from '../../contexts/TopicBarContext';
import { MessageSquare } from 'lucide-react';

interface GlobalSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// Flat item for keyboard navigation
type ResultItem =
  | { kind: 'document'; hit: SearchHit<DocumentHit> }
  | { kind: 'issue'; hit: SearchHit<IssueHit> }
  | { kind: 'sprint'; hit: SearchHit<SprintHit> }
  | { kind: 'email'; hit: SearchHit<EmailThreadHit> }
  | { kind: 'topic'; hit: SearchHit<TopicHit> }
  | { kind: 'user'; hit: SearchHit<UserHit> };

// A project group with its items
interface ProjectGroup {
  projectName: string;
  projectId: string;
  items: ResultItem[];
  latestTimestamp: number;
}

// --- Helpers ---

function relativeTime(ts?: number): string | null {
  if (!ts) return null;
  const now = Date.now();
  // Typesense stores epoch seconds; convert if needed
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const diff = now - ms;
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  // Show date for older items
  const d = new Date(ms);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function getItemTimestamp(item: ResultItem): number {
  const doc = item.hit.document;
  if (item.kind === 'user') return (doc as UserHit).created_at ?? 0;
  if (item.kind === 'sprint') {
    const s = doc as SprintHit;
    return s.completed_at ?? s.started_at ?? s.created_at ?? 0;
  }
  if (item.kind === 'email') {
    const e = doc as EmailThreadHit;
    return e.last_message_at ?? e.created_at ?? 0;
  }
  if (item.kind === 'topic') {
    const t = doc as TopicHit;
    return t.updated_at ?? t.created_at ?? 0;
  }
  const d = doc as DocumentHit | IssueHit;
  return d.updated_at ?? d.created_at ?? 0;
}

function docTypeIcon(docType?: string) {
  switch (docType) {
    case 'spec':
      return <BookOpen size={14} className="text-indigo-500" />;
    case 'test':
      return <FlaskConical size={14} className="text-emerald-500" />;
    default:
      return <FileText size={14} className="text-neutral-400" />;
  }
}

function docTypeBadge(docType?: string) {
  switch (docType) {
    case 'spec':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">Spec</span>;
    case 'test':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">Test</span>;
    case 'guide':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">Guide</span>;
    default:
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">Doc</span>;
  }
}

function issueTypeIcon(type?: string) {
  switch (type) {
    case 'bug':
      return <Bug size={14} className="text-red-500" />;
    case 'feature':
      return <Sparkles size={14} className="text-violet-500" />;
    default:
      return <Circle size={14} className="text-neutral-400" />;
  }
}

function issueStatusBadge(status?: string) {
  switch (status) {
    case 'done':
    case 'closed':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">{status}</span>;
    case 'in_progress':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">in progress</span>;
    default:
      return status ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">{status}</span> : null;
  }
}

function sprintStatusBadge(status?: string) {
  switch (status) {
    case 'completed':
    case 'succeeded':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">completed</span>;
    case 'building':
    case 'running':
    case 'in_progress':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">building</span>;
    case 'failed':
    case 'error':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 font-medium">failed</span>;
    case 'cancelled':
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">cancelled</span>;
    default:
      return status ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">{status}</span> : null;
  }
}

function priorityDot(priority?: string) {
  const colors: Record<string, string> = {
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-blue-400',
  };
  if (!priority || !colors[priority]) return null;
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[priority]}`} title={priority} />;
}

function highlightSnippet(hit: SearchHit<unknown>): string | null {
  const h = hit.highlights?.find(h => h.snippet);
  return h?.snippet || null;
}

function buildProjectGroups(results: GlobalSearchResponse): { groups: ProjectGroup[]; topics: ResultItem[]; emails: ResultItem[]; users: ResultItem[] } {
  const groupMap = new Map<string, ProjectGroup>();
  const topics: ResultItem[] = [];
  const emails: ResultItem[] = [];
  const users: ResultItem[] = [];

  // Collect documents
  for (const hit of results.documents.hits) {
    const doc = hit.document;
    const key = doc.project_name ?? doc.project_id;
    if (!groupMap.has(key)) {
      groupMap.set(key, { projectName: doc.project_name ?? doc.project_id, projectId: doc.project_id, items: [], latestTimestamp: 0 });
    }
    const item: ResultItem = { kind: 'document', hit };
    groupMap.get(key)!.items.push(item);
  }

  // Collect issues
  for (const hit of results.issues.hits) {
    const issue = hit.document;
    const key = issue.project_name ?? issue.project_id;
    if (!groupMap.has(key)) {
      groupMap.set(key, { projectName: issue.project_name ?? issue.project_id, projectId: issue.project_id, items: [], latestTimestamp: 0 });
    }
    const item: ResultItem = { kind: 'issue', hit };
    groupMap.get(key)!.items.push(item);
  }

  // Collect sprints
  if (results.sprints?.hits) {
    for (const hit of results.sprints.hits) {
      const sprint = hit.document;
      const key = sprint.project_name ?? sprint.project_id;
      if (!groupMap.has(key)) {
        groupMap.set(key, { projectName: sprint.project_name ?? sprint.project_id, projectId: sprint.project_id, items: [], latestTimestamp: 0 });
      }
      const item: ResultItem = { kind: 'sprint', hit };
      groupMap.get(key)!.items.push(item);
    }
  }

  // Collect topics
  if (results.topics?.hits) {
    for (const hit of results.topics.hits) {
      topics.push({ kind: 'topic', hit });
    }
  }

  // Collect emails
  if (results.emails?.hits) {
    for (const hit of results.emails.hits) {
      emails.push({ kind: 'email', hit });
    }
  }

  // Collect users
  for (const hit of results.users.hits) {
    users.push({ kind: 'user', hit });
  }

  // Sort items within each group by timestamp desc, compute latest
  for (const group of groupMap.values()) {
    group.items.sort((a, b) => getItemTimestamp(b) - getItemTimestamp(a));
    group.latestTimestamp = group.items.length > 0 ? getItemTimestamp(group.items[0]) : 0;
  }

  // Sort groups by most recent activity
  const groups = Array.from(groupMap.values()).sort((a, b) => b.latestTimestamp - a.latestTimestamp);

  return { groups, topics, emails, users };
}

const RECENT_SEARCHES_KEY = 'martha:recentSearches';
const MAX_RECENT_SEARCHES = 5;

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveRecentSearch(query: string) {
  const trimmed = query.trim();
  if (trimmed.length < 2) return;
  const searches = getRecentSearches().filter(s => s !== trimmed);
  searches.unshift(trimmed);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)));
}

export function GlobalSearchDialog({ isOpen, onClose }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const { allProjects } = useProject();
  const { wsSlug, currentWorkspace } = useWorkspace();
  const { allTopics, focusTopic } = useTopicBar();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<RecentItemsResponse | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Recent topics — sorted by updated_at, top 5 active
  const recentTopics = useMemo(() => {
    return [...allTopics]
      .filter(t => t.status === 'active')
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);
  }, [allTopics]);

  // Build grouped structure + flat item list for keyboard nav
  const { groups, topicItems, emailItems, userItems, items } = useMemo(() => {
    if (!results) return { groups: [], topicItems: [] as ResultItem[], emailItems: [] as ResultItem[], userItems: [] as ResultItem[], items: [] as ResultItem[] };
    const { groups, topics, emails, users } = buildProjectGroups(results);
    // Flatten: topics first, then project items in display order, then emails, then users
    const flat: ResultItem[] = [];
    for (const t of topics) flat.push(t);
    for (const group of groups) {
      for (const item of group.items) flat.push(item);
    }
    for (const e of emails) flat.push(e);
    for (const u of users) flat.push(u);
    return { groups, topicItems: topics, emailItems: emails, userItems: users, items: flat };
  }, [results]);

  // Focus input when opened, fetch recents
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
      setQuery('');
      setResults(null);
      setActiveIndex(0);
      setRecentSearches(getRecentSearches());

      // Fetch recent items from search-service
      searchApi.recents(currentWorkspace?.id).then(setRecentItems).catch(() => {});
    }
  }, [isOpen]);

  // Debounced search
  const doSearch = useCallback((q: string) => {
    abortRef.current?.abort();
    clearTimeout(debounceRef.current);

    if (q.trim().length < 2) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const data = await searchApi.global(q.trim(), { per_page: 8, signal: controller.signal });
        if (!controller.signal.aborted) {
          setResults(data);
          setActiveIndex(0);
          saveRecentSearch(q.trim());
        }
      } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          console.error('[GlobalSearch]', err);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 200);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    doSearch(value);
  };

  // Navigate to selected item
  const selectItem = useCallback((item: ResultItem) => {
    if (item.kind === 'document') {
      const doc = item.hit.document;
      const project = allProjects.find(p => p.id === doc.project_id);
      if (project) {
        const shortId = getProjectShortId(project.id);
        const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
        navigate(`${base}/documents/${doc.id}`);
      }
    } else if (item.kind === 'issue') {
      const issue = item.hit.document;
      const project = allProjects.find(p => p.id === issue.project_id);
      if (project && issue.board_id) {
        const shortId = getProjectShortId(project.id);
        const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
        navigate(`${base}/boards/${issue.board_id}/is/${issue.id}`);
      }
    } else if (item.kind === 'sprint') {
      const sprint = item.hit.document;
      const project = allProjects.find(p => p.id === sprint.project_id);
      if (project) {
        const shortId = getProjectShortId(project.id);
        const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
        navigate(`${base}/monitor?sprint=${sprint.id}`);
      }
    } else if (item.kind === 'topic') {
      const topic = item.hit.document;
      focusTopic(topic.id);
    } else if (item.kind === 'email') {
      const email = item.hit.document;
      const base = wsSlug ? `/w/${wsSlug}` : '';
      navigate(`${base}/cr/in/${email.id}`);
    }
    // Users: no navigation
    onClose();
  }, [allProjects, navigate, onClose, focusTopic]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => {
        const next = Math.min(i + 1, items.length - 1);
        scrollToItem(next);
        return next;
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => {
        const next = Math.max(i - 1, 0);
        scrollToItem(next);
        return next;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (items[activeIndex]) {
        selectItem(items[activeIndex]);
      }
      return;
    }
  };

  const scrollToItem = (index: number) => {
    const el = listRef.current?.querySelector(`[data-index="${index}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  };

  if (!isOpen) return null;

  // Track flat index across all groups for keyboard nav
  let flatIndex = -1;

  const totalResults = items.length;

  return (
    <div
      className="fixed inset-0 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="absolute w-[640px] max-h-[min(680px,80vh)] bg-white rounded-xl shadow-2xl border border-neutral-200/80 flex flex-col overflow-hidden"
        style={{ left: '50%', transform: 'translateX(-50%)', top: '120px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
          <Search size={18} className="text-neutral-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search topics, documents, issues, emails..."
            className="flex-1 text-[15px] text-neutral-800 placeholder-neutral-400 bg-transparent border-b border-transparent outline-none focus:border-coral-400/40 transition-colors"
          />
          {isLoading && <Loader2 size={16} className="animate-spin text-neutral-400" />}
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain">
          {query.trim().length < 2 ? (
            <div className="py-2">
              {/* Recent searches */}
              {recentSearches.length > 0 && (
                <div className="px-5 pt-3 pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Search size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Recent Searches
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {recentSearches.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setQuery(s); doSearch(s); }}
                        className="text-xs px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600 hover:bg-coral-50 hover:text-coral-700 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Topics */}
              {recentTopics.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <MessageSquare size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Recent Topics
                    </span>
                  </div>
                  {recentTopics.map((topic) => {
                    const ts = new Date(topic.updated_at).getTime();
                    const timeStr = relativeTime(Math.floor(ts / 1000));
                    return (
                      <button
                        key={topic.id}
                        onClick={() => { focusTopic(topic.id); onClose(); }}
                        className="w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer hover:bg-coral-50/80"
                      >
                        <span className="flex-shrink-0">
                          <MessageSquare size={14} className="text-coral-400" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate text-neutral-800">
                              {topic.title}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">active</span>
                            {topic.items.length > 0 && (
                              <span className="text-[10px] text-neutral-400">{topic.items.length} items</span>
                            )}
                          </div>
                          {topic.description && (
                            <p className="text-xs text-neutral-500 truncate mt-0.5">{topic.description}</p>
                          )}
                        </div>
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">
                            {timeStr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Recent Documents */}
              {recentItems?.documents?.hits && recentItems.documents.hits.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <FileText size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Recent Documents
                    </span>
                  </div>
                  {recentItems.documents.hits.map((hit) => {
                    const doc = hit.document;
                    const timeStr = relativeTime(doc.updated_at ?? doc.created_at);
                    return (
                      <button
                        key={doc.id}
                        onClick={() => {
                          const project = allProjects.find(p => p.id === doc.project_id);
                          if (project) {
                            const shortId = getProjectShortId(project.id);
                            const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
                            navigate(`${base}/documents/${doc.id}`);
                          }
                          onClose();
                        }}
                        className="w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer hover:bg-coral-50/80"
                      >
                        <span className="flex-shrink-0">{docTypeIcon(doc.doc_type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate text-neutral-800">{doc.title}</span>
                            {docTypeBadge(doc.doc_type)}
                          </div>
                        </div>
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">{timeStr}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Recent Issues */}
              {recentItems?.issues?.hits && recentItems.issues.hits.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <CheckCircle2 size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Recent Issues
                    </span>
                  </div>
                  {recentItems.issues.hits.map((hit) => {
                    const issue = hit.document;
                    const timeStr = relativeTime(issue.updated_at ?? issue.created_at);
                    return (
                      <button
                        key={issue.id}
                        onClick={() => {
                          const project = allProjects.find(p => p.id === issue.project_id);
                          if (project && issue.board_id) {
                            const shortId = getProjectShortId(project.id);
                            const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
                            navigate(`${base}/boards/${issue.board_id}/is/${issue.id}`);
                          }
                          onClose();
                        }}
                        className="w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer hover:bg-coral-50/80"
                      >
                        <span className="flex-shrink-0">{issueTypeIcon(issue.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate text-neutral-800">{issue.title}</span>
                            {issueStatusBadge(issue.status)}
                            {priorityDot(issue.priority)}
                          </div>
                        </div>
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">{timeStr}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Empty state fallback */}
              {recentTopics.length === 0 && !recentItems && recentSearches.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
                  <Search size={32} className="mb-3 text-neutral-300" />
                  <p className="text-sm">Type to search across Martha</p>
                </div>
              )}

              {(recentTopics.length > 0 || recentItems) && (
                <div className="px-5 pt-4 pb-3 text-center">
                  <p className="text-xs text-neutral-400">Type to search documents, issues, emails, and more</p>
                </div>
              )}
            </div>
          ) : !isLoading && results && totalResults === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
              <p className="text-sm">No results for &ldquo;{query}&rdquo;</p>
            </div>
          ) : totalResults > 0 ? (
            <div className="py-2">
              {/* Topics section */}
              {topicItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <MessageSquare size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Topics
                    </span>
                    <span className="text-[10px] font-normal text-neutral-300">
                      ({topicItems.length})
                    </span>
                  </div>
                  {topicItems.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isActive = idx === activeIndex;
                    const ts = getItemTimestamp(item);
                    const timeStr = relativeTime(ts);

                    return (
                      <button
                        key={`topic-${item.hit.document.id}`}
                        data-index={idx}
                        onClick={() => selectItem(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer
                          ${isActive ? 'bg-coral-50/80' : 'hover:bg-neutral-50'}`}
                      >
                        <TopicRow hit={item.hit as SearchHit<TopicHit>} isActive={isActive} />
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">
                            {timeStr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Project groups */}
              {groups.map((group) => (
                <div key={group.projectId}>
                  {/* Project header */}
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <FolderOpen size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      {group.projectName}
                    </span>
                    <span className="text-[10px] font-normal text-neutral-300">
                      ({group.items.length})
                    </span>
                  </div>

                  {group.items.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isActive = idx === activeIndex;
                    const ts = getItemTimestamp(item);
                    const timeStr = relativeTime(ts);

                    return (
                      <button
                        key={`${item.kind}-${item.hit.document.id}`}
                        data-index={idx}
                        onClick={() => selectItem(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer
                          ${isActive ? 'bg-coral-50/80' : 'hover:bg-neutral-50'}`}
                      >
                        {item.kind === 'document' && <DocumentRow hit={item.hit} isActive={isActive} />}
                        {item.kind === 'issue' && <IssueRow hit={item.hit} isActive={isActive} />}
                        {item.kind === 'sprint' && <SprintRow hit={item.hit} isActive={isActive} />}
                        {item.kind === 'email' && <EmailRow hit={item.hit as SearchHit<EmailThreadHit>} isActive={isActive} />}
                        {item.kind === 'topic' && <TopicRow hit={item.hit as SearchHit<TopicHit>} isActive={isActive} />}
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">
                            {timeStr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}

              {/* Emails section */}
              {emailItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-5 pt-3 pb-1.5">
                    <Mail size={12} className="text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                      Emails
                    </span>
                    <span className="text-[10px] font-normal text-neutral-300">
                      ({emailItems.length})
                    </span>
                  </div>
                  {emailItems.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isActive = idx === activeIndex;
                    const ts = getItemTimestamp(item);
                    const timeStr = relativeTime(ts);

                    return (
                      <button
                        key={`email-${item.hit.document.id}`}
                        data-index={idx}
                        onClick={() => selectItem(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer
                          ${isActive ? 'bg-coral-50/80' : 'hover:bg-neutral-50'}`}
                      >
                        <EmailRow hit={item.hit as SearchHit<EmailThreadHit>} isActive={isActive} />
                        {timeStr && (
                          <span className="flex-shrink-0 text-[10px] text-neutral-400 tabular-nums ml-auto">
                            {timeStr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Users section */}
              {userItems.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
                    Users <span className="font-normal text-neutral-300">({userItems.length})</span>
                  </div>
                  {userItems.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isActive = idx === activeIndex;

                    return (
                      <button
                        key={`user-${item.hit.document.id}`}
                        data-index={idx}
                        onClick={() => selectItem(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors cursor-pointer
                          ${isActive ? 'bg-coral-50/80' : 'hover:bg-neutral-50'}`}
                      >
                        <UserRow hit={item.hit as SearchHit<UserHit>} isActive={isActive} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-5 py-2.5 border-t border-neutral-100 bg-neutral-50/50 text-[11px] text-neutral-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white rounded border border-neutral-200 text-[10px] font-mono">&uarr;&darr;</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white rounded border border-neutral-200 text-[10px] font-mono">&crarr;</kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white rounded border border-neutral-200 text-[10px] font-mono">esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}

// --- Row components ---

function DocumentRow({ hit, isActive }: { hit: SearchHit<DocumentHit>; isActive: boolean }) {
  const doc = hit.document;
  const snippet = highlightSnippet(hit);
  return (
    <>
      <span className="flex-shrink-0">{docTypeIcon(doc.doc_type)}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {doc.title}
          </span>
          {docTypeBadge(doc.doc_type)}
        </div>
        {snippet && (
          <p
            className="text-xs text-neutral-500 truncate mt-0.5"
            dangerouslySetInnerHTML={{ __html: snippet }}
          />
        )}
      </div>
    </>
  );
}

function IssueRow({ hit, isActive }: { hit: SearchHit<IssueHit>; isActive: boolean }) {
  const issue = hit.document;
  return (
    <>
      <span className="flex-shrink-0">{issueTypeIcon(issue.type)}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {issue.title}
          </span>
          {issueStatusBadge(issue.status)}
          {priorityDot(issue.priority)}
        </div>
      </div>
    </>
  );
}

function SprintRow({ hit, isActive }: { hit: SearchHit<SprintHit>; isActive: boolean }) {
  const sprint = hit.document;
  return (
    <>
      <span className="flex-shrink-0">
        <Zap size={14} className="text-amber-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {sprint.name}
          </span>
          {sprintStatusBadge(sprint.status)}
          {sprint.epic_count != null && (
            <span className="text-[10px] text-neutral-400">{sprint.epic_count} epics</span>
          )}
        </div>
      </div>
    </>
  );
}

function TopicRow({ hit, isActive }: { hit: SearchHit<TopicHit>; isActive: boolean }) {
  const topic = hit.document;
  return (
    <>
      <span className="flex-shrink-0">
        <MessageSquare size={14} className="text-coral-400" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {topic.title}
          </span>
          {topic.status && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              topic.status === 'active' ? 'bg-emerald-50 text-emerald-600' :
              topic.status === 'completed' ? 'bg-neutral-100 text-neutral-500' :
              'bg-neutral-100 text-neutral-400'
            }`}>{topic.status}</span>
          )}
          {topic.assigned_agents && topic.assigned_agents.length > 0 && (
            <span className="text-[10px] text-neutral-400">{topic.assigned_agents.length} agents</span>
          )}
        </div>
        {topic.description && (
          <p className="text-xs text-neutral-500 truncate mt-0.5">{topic.description}</p>
        )}
      </div>
    </>
  );
}

function EmailRow({ hit, isActive }: { hit: SearchHit<EmailThreadHit>; isActive: boolean }) {
  const email = hit.document;
  const snippet = highlightSnippet(hit);
  return (
    <>
      <span className="flex-shrink-0">
        <Mail size={14} className="text-blue-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {email.subject || '(no subject)'}
          </span>
          {email.from_address && (
            <span className="text-[10px] text-neutral-400 truncate flex-shrink-0">{email.from_address}</span>
          )}
        </div>
        {snippet && (
          <p
            className="text-xs text-neutral-500 truncate mt-0.5"
            dangerouslySetInnerHTML={{ __html: snippet }}
          />
        )}
      </div>
    </>
  );
}

function UserRow({ hit, isActive }: { hit: SearchHit<UserHit>; isActive: boolean }) {
  const user = hit.document;
  return (
    <>
      <span className="flex-shrink-0">
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center">
            <User size={12} className="text-neutral-500" />
          </div>
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-coral-700' : 'text-neutral-800'}`}>
            {user.display_name}
          </span>
          {user.role && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">
              {user.role}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 truncate">{user.email}</p>
      </div>
    </>
  );
}
